<template>
  <FilterContainer class="pt-0 gap-5" :class="isOpen ? 'pb-5' : ''">
    <template v-if="isLoading">
      <button
        class="text-extra-text-color rounded-[10px] w-full h-[60px] bg-gray-light/60 animate-pulse"
      >
        Поиск по типоразмеру
      </button>
    </template>
    <template v-else>
      <button
        class="w-full text-white cursor-pointer font-helvetica-700 text-[16px]"
        :class="[
          isOpen ? 'bg-gray-dark' : 'bg-orange',
          props.mobile
            ? 'h-[52px] rounded-[14px]'
            : 'h-[60px] max-w-[373px] rounded-[10px]',
        ]"
        @click="isOpen = !isOpen"
      >
        Поиск по типоразмеру
      </button>

      <Transition name="slide-filters">
        <div v-if="isOpen" class="flex flex-col gap-3">
          <RadioFilter
            :values="diameterValues"
            :mobile="props.mobile"
            :open="true"
            @update:values="onUpdateDiameterValues"
          >
            {{ typeSizeFilters.diameter.displayName }}
          </RadioFilter>

          <Filter
            :values="sizeFilterValues"
            :mobile="props.mobile"
            :open="true"
            @update:values="onUpdateSizeValues"
          >
            {{ typeSizeFilters.size.displayName }}
          </Filter>
        </div>
      </Transition>
      <button
        v-if="isOpen"
        class="mt-1 w-full self-center rounded-[14px] bg-orange text-[16px] font-helvetica-700 text-white hover:bg-selected-orange cursor-pointer"
        :class="
          props.mobile ? 'h-[52px]' : 'h-[55px] max-w-[333px] rounded-[10px]'
        "
        @click="startSearch"
      >
        Найти шины
      </button>
    </template>
  </FilterContainer>
</template>

<script setup lang="ts">
import Filter from "~/components/goods/Filter.vue";
import RadioFilter from "~/components/goods/RadioFilter.vue";
import FilterContainer from "~/components/goods/FilterContainer.vue";
import { useFacetedFiltersApi } from "~/composables/useFacetedFiltersApi";
import type { FilterValues } from "~/types/filterValues";
import { getTypeSizeFilterDefinitions } from "~/utils/facetedFilterDefinitions";

const facetedFiltersApi = useFacetedFiltersApi();

const props = defineProps<{
  code: number;
  initialDiameter?: string;
  initialSizes?: string[];
  mobile?: boolean;
}>();

const emit = defineEmits<{
  (e: "search", payload: { sizes: string[]; diameter: string }): void;
  (e: "search-start"): void;
}>();

const isOpen = ref(false);
const isLoading = ref(true);
const selectedDiameter = ref("");
const selectedSizeValues = ref<string[]>([]);
const diameterValues = ref<FilterValues[]>([]);
const allSizeValues = ref<FilterValues[]>([]);
const typeSizeFilters = computed(() =>
  getTypeSizeFilterDefinitions(props.code),
);

const mapFacetValues = (
  values: Array<{ value: string; count: number }> | undefined,
  selected: Set<string>,
) =>
  (Array.isArray(values) ? values : [])
    .map((item) => {
      const label = String(item?.value ?? "");
      if (!label) return null;

      return {
        value: label,
        label,
        count:
          typeof item?.count === "number"
            ? item.count
            : Number(item?.count ?? 0),
        selected: selected.has(label),
      };
    })
    .filter((value): value is FilterValues => Boolean(value));

const ensureSelectedValues = (
  values: FilterValues[],
  selected: Set<string>,
) => {
  const existing = new Set(values.map((value) => value.value));
  selected.forEach((value) => {
    if (!existing.has(value)) {
      values.push({
        value,
        label: value,
        count: 0,
        selected: true,
      });
    }
  });
  return values;
};

const refreshTypeSizeValues = async () => {
  if (!facetedFiltersApi.isConfigured) return;

  try {
    const data = await facetedFiltersApi.getFilters({
      groupId: props.code,
      need: [
        typeSizeFilters.value.diameter.needName,
        typeSizeFilters.value.size.needName,
      ],
      selected: selectedDiameter.value
        ? {
            [typeSizeFilters.value.diameter.needName]: [selectedDiameter.value],
          }
        : {},
    });
    const selectedDiameters = new Set(
      selectedDiameter.value ? [selectedDiameter.value] : [],
    );
    const selectedSizes = new Set(selectedSizeValues.value);

    diameterValues.value = ensureSelectedValues(
      mapFacetValues(
        data[typeSizeFilters.value.diameter.needName],
        selectedDiameters,
      ),
      selectedDiameters,
    );
    allSizeValues.value = ensureSelectedValues(
      mapFacetValues(data[typeSizeFilters.value.size.needName], selectedSizes),
      selectedSizes,
    );
  } catch (error) {
    console.error("Error fetching faceted sizes", error);
  }
};

const sizeFilterValues = computed<FilterValues[]>(() => allSizeValues.value);

const onUpdateDiameterValues = (updated: FilterValues[]) => {
  const selected = updated.find((d) => d.selected);
  const selectedValue = selected?.value ?? "";
  diameterValues.value = updated.map((v) => ({
    ...v,
    selected: v.value === selectedValue,
  }));
  selectedDiameter.value = selectedValue;
  selectedSizeValues.value = [];
  allSizeValues.value = allSizeValues.value.map((v) => ({
    ...v,
    selected: false,
  }));
  void refreshTypeSizeValues();
};

const onUpdateSizeValues = (updatedVisible: FilterValues[]) => {
  const updatedMap = new Map(updatedVisible.map((v) => [v.value, v.selected]));

  allSizeValues.value = allSizeValues.value.map((v) =>
    updatedMap.has(v.value) ? { ...v, selected: updatedMap.get(v.value)! } : v,
  );
  selectedSizeValues.value = allSizeValues.value
    .filter((v) => v.selected)
    .map((v) => v.value);
};

const selectedSizes = computed(() => selectedSizeValues.value);

const syncExternalSelection = () => {
  const diameter = props.initialDiameter?.trim() ?? "";
  const sizes = (props.initialSizes ?? []).filter(Boolean);
  const selectedSizesSet = new Set(sizes);

  selectedDiameter.value = diameter;
  selectedSizeValues.value = sizes;
  diameterValues.value = diameterValues.value.map((value) => ({
    ...value,
    selected: Boolean(diameter) && value.value === diameter,
  }));
  allSizeValues.value = allSizeValues.value.map((value) => ({
    ...value,
    selected: selectedSizesSet.has(value.value),
  }));
};

onMounted(async () => {
  try {
    syncExternalSelection();
    await refreshTypeSizeValues();
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => [props.initialDiameter ?? "", (props.initialSizes ?? []).join(";")],
  async () => {
    if (isLoading.value) return;
    syncExternalSelection();
    await refreshTypeSizeValues();
  },
  { immediate: true },
);

const startSearch = () => {
  emit("search-start");
  emit("search", {
    sizes: selectedSizes.value,
    diameter: selectedDiameter.value,
  });
};
</script>

<style scoped>
.slide-filters-enter-active,
.slide-filters-leave-active {
  overflow: hidden;
  transition:
    max-height 0.5s ease,
    opacity 0.25s ease,
    transform 0.55s ease;
}

.slide-filters-enter-from,
.slide-filters-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-8px);
}

.slide-filters-enter-to,
.slide-filters-leave-from {
  max-height: 700px;
  /* достаточно, чтобы уместить оба фильтра */
  opacity: 1;
  transform: translateY(0);
}
</style>
